import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import * as React from "react";

import type { ChecklistItem, ChecklistRowApplicability } from "@/api/plan_review";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

// A proposed requirement row while the planner reviews it (ENG-2005). rowId
// is a client-side handle for React keys and edits: a row the planner adds
// has no item key until the server derives one at confirm. proposedLabel and
// proposedLookFor are the wording the proposal carried, so a numeric rule
// read off the label and the documents a look-for cites (ENG-2004) are shown
// while they still match and come back after an undone edit.
export type ProposalRow = ChecklistItem & { rowId: string; proposedLabel?: string; proposedLookFor?: string | null };

const APPLICABILITY_OPTIONS: Array<{ value: ChecklistRowApplicability; label: string }> = [
  { value: "required", label: "Required" },
  { value: "optional", label: "Optional" },
  { value: "not_applicable", label: "N/A" },
];

let nextRowId = 0;
function newRowId() {
  nextRowId += 1;
  return `row-${nextRowId}`;
}

export function toProposalRows(items: ChecklistItem[]): ProposalRow[] {
  return items.map((item) => ({
    ...item,
    applicability: item.applicability ?? (item.required ? "required" : "optional"),
    rowId: newRowId(),
    proposedLabel: item.label,
    proposedLookFor: item.look_for ?? null,
  }));
}

/**
 * Whitespace the way ConfirmChecklistExtraction compares text: any run of
 * whitespace as one space, then trimmed (Rails' squish). "Unchanged" here has
 * to mean what it means at confirm, or the editor retires a rule or a sources
 * line on screen that the saved row keeps (ENG-2139). The set is Ruby's
 * [[:space:]], which is JavaScript's \s without the byte-order mark (U+FEFF)
 * and with the next-line control (U+0085); both can ride in on pasted text.
 */
const RUBY_SPACE = /[\t\n\v\f\r \u0085\u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]+/g;
function squish(text: string): string {
  return text.replace(RUBY_SPACE, " ").replace(/^ | $/g, "");
}

/** A numeric rule was read off the proposed wording, so it only applies while the label still says it. */
export function numericRuleActive(row: ProposalRow): boolean {
  return (
    row.verification_mode === "numeric_rule" &&
    !!row.numeric_rule &&
    row.proposedLabel !== undefined &&
    squish(row.label) === squish(row.proposedLabel)
  );
}

/**
 * Citations belong to the requirement and the guidance the research pass
 * wrote about it, so editing either retires them. The server makes the same
 * call at confirm; this keeps the screen from claiming sources the saved row
 * will not have, and from disowning sources it will keep.
 */
export function citationsActive(row: ProposalRow): boolean {
  if (!row.citations?.length) return false;
  if (row.proposedLabel === undefined || squish(row.label) !== squish(row.proposedLabel)) return false;
  return squish(row.look_for ?? "") === squish(row.proposedLookFor ?? "");
}

/**
 * The index of the row each row sits under (ENG-2162), or null. A link
 * counts only while its parent is still in the list above the row: a
 * removed parent, or one moved below its child, leaves the child top-level.
 * Derived rather than cleared on edit, so moving a row back restores its
 * link, and the server applies the same rule at confirm.
 */
export function parentIndexesFor(rows: Array<Pick<ChecklistItem, "key" | "parent_key">>): Array<number | null> {
  const firstIndex = new Map<string, number>();
  return rows.map((row, index) => {
    const parent = row.parent_key ? firstIndex.get(row.parent_key) : undefined;
    if (row.key && !firstIndex.has(row.key)) firstIndex.set(row.key, index);
    return parent ?? null;
  });
}

/** The rows as the confirm endpoint wants them: no client handles, and a parent link only while it holds. */
export function toChecklistItems(rows: ProposalRow[]): ChecklistItem[] {
  const parents = parentIndexesFor(rows);
  return rows.map(
    ({ rowId: _rowId, proposedLabel: _proposedLabel, proposedLookFor: _proposedLookFor, ...item }, index) => {
      const row = rows[index];
      const { parent_key: _parentKey, ...unlinked } = item;
      const parentIndex = parents[index];
      const linked = parentIndex === null ? unlinked : { ...unlinked, parent_key: rows[parentIndex].key };
      const base = { ...linked, label: item.label.trim(), required: item.applicability === "required" };
      if (item.verification_mode !== "numeric_rule" || numericRuleActive(row)) return base;
      // The label no longer says what the rule was read off; the server
      // makes the same call and restores the rule if the label is put back.
      const { numeric_rule: _rule, ...rest } = base;
      return { ...rest, verification_mode: "presence_with_value" };
    },
  );
}

const NUMERIC_OPERATORS: Record<string, string> = { gt: ">", gte: "≥", lt: "<", lte: "≤", eq: "=" };

/** "≥ 25" for a row the model read a numeric threshold off. */
export function describeNumericRule(rule: ChecklistItem["numeric_rule"]): string | null {
  if (!rule) return null;
  return `${NUMERIC_OPERATORS[rule.operator] ?? rule.operator} ${rule.value}`;
}


/**
 * The heading to show above each row: only where the section changes from
 * the last one shown, so a row with no section (one a planner added) does
 * not make the row after it repeat its heading. Shared with the read-only
 * ChecklistDetailDialog so both read like the source document.
 */
export function sectionHeadingsFor(items: Array<Pick<ChecklistItem, "section">>): Array<string | null> {
  let lastSection: string | null = null;
  return items.map((item) => {
    const section = item.section?.trim() || null;
    if (!section || section === lastSection) return null;
    lastSection = section;
    return section;
  });
}

export function blankProposalRow(): ProposalRow {
  return {
    rowId: newRowId(),
    label: "",
    required: true,
    applicability: "required",
    verification_mode: "presence_only",
    origin: "planner",
  };
}

/**
 * Look-for guidance runs one to three sentences, and a fixed two-row box
 * clipped the third line on most rows, hiding the part that carries the
 * local dimension. This grows to whatever its content needs.
 */
function AutoGrowTextarea({ value, ...props }: React.ComponentProps<typeof Textarea>) {
  const ref = React.useRef<HTMLTextAreaElement>(null);

  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    // jsdom reports 0 here; the CSS min-height stays in charge there.
    if (el.scrollHeight === 0) return;
    // scrollHeight measures the content box, and the box-sizing is
    // border-box, so assigning it straight leaves the content short by the
    // borders. Nothing overflows visibly at this font size, but with
    // overflow hidden there would be no scrollbar to reveal it if a larger
    // one ever did.
    const borders = el.offsetHeight - el.clientHeight;
    el.style.height = `${el.scrollHeight + borders}px`;
  }, [value]);

  return <Textarea ref={ref} value={value} {...props} />;
}

function moveRow(rows: ProposalRow[], index: number, delta: -1 | 1): ProposalRow[] {
  const target = index + delta;
  if (target < 0 || target >= rows.length) return rows;
  const next = [...rows];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

const MAX_INDENT_DEPTH = 3;

type ChecklistProposalEditorProps = {
  rows: ProposalRow[];
  onChange: (rows: ProposalRow[]) => void;
  disabled?: boolean;
};

// Editable proposal rows, then confirm: the same shape as the Grant plan
// review editor. All state is the caller's; nothing here persists.
export function ChecklistProposalEditor({ rows, onChange, disabled = false }: ChecklistProposalEditorProps) {
  const patchRow = (rowId: string, patch: Partial<ProposalRow>) => {
    onChange(rows.map((row) => (row.rowId === rowId ? { ...row, ...patch } : row)));
  };

  const sectionHeadings = sectionHeadingsFor(rows);
  const parentIndexes = parentIndexesFor(rows);
  // Nesting depth for the indent, capped so a deep chain cannot squeeze
  // the label field on a narrow dialog.
  const depths: number[] = [];
  parentIndexes.forEach((parentIndex, index) => {
    depths[index] = parentIndex === null ? 0 : Math.min(depths[parentIndex] + 1, MAX_INDENT_DEPTH);
  });

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Edit any label or its look-for guidance, set whether each item is required, remove what does not apply,
        and add anything the document missed. Nothing is saved until you confirm.
      </p>
      <ol
        className="w-full min-w-0 rounded-md border border-border text-sm"
        data-testid="proposed-rows"
        aria-label="Proposed requirement rows"
      >
        {rows.map((row, index) => {
          const sectionHeading = sectionHeadings[index];
          const position = index + 1;
          const sourceText = row.source_text?.trim();
          const showSource = !!sourceText && sourceText !== row.label.trim();
          // The check's reason is about the document's wording; once the
          // planner rewrites the label it no longer describes the row.
          const originReason = row.origin === "source" && !showSource ? row.origin_reason?.trim() : "";
          const numericRule = numericRuleActive(row) ? describeNumericRule(row.numeric_rule) : null;
          const citedDocuments = citationsActive(row)
            ? (row.citations ?? []).map((citation) => citation.document_name).filter(Boolean)
            : [];
          const parentIndex = parentIndexes[index];
          const parentLabel = parentIndex === null ? "" : rows[parentIndex].label.trim();
          return (
            <li key={row.rowId} className="border-b border-border last:border-b-0" data-testid="proposed-row">
              {sectionHeading ? (
                <div className="bg-muted/50 px-3 py-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {sectionHeading}
                </div>
              ) : null}
              <div className="flex items-start gap-2 px-3 py-2">
                <span className="w-7 shrink-0 pt-2 tabular-nums text-muted-foreground">{position}.</span>
                <div
                  className="min-w-0 flex-1 space-y-1"
                  style={depths[index] ? { paddingLeft: `${depths[index] * 1.25}rem` } : undefined}
                >
                  {parentIndex !== null ? (
                    // The line this row sits under in the document (ENG-2162),
                    // so the planner can check the link the research pass used.
                    <p
                      className="min-w-0 truncate text-xs text-muted-foreground"
                      title={`Part of row ${parentIndex + 1}: “${parentLabel}”`}
                      id={`${row.rowId}-parent`}
                      data-testid="proposed-row-parent"
                    >
                      Part of row {parentIndex + 1}: “{parentLabel}”
                    </p>
                  ) : null}
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <Input
                      value={row.label}
                      onChange={(event) => patchRow(row.rowId, { label: event.target.value })}
                      disabled={disabled}
                      aria-label={`Label for row ${position}`}
                      aria-describedby={parentIndex !== null ? `${row.rowId}-parent` : undefined}
                      placeholder="Requirement"
                      className="min-w-0 flex-1"
                    />
                    <select
                      className="h-9 rounded-md border border-input bg-background px-2 text-sm sm:w-28"
                      value={row.applicability ?? "required"}
                      disabled={disabled}
                      aria-label={`Applicability for row ${position}`}
                      onChange={(event) =>
                        patchRow(row.rowId, { applicability: event.target.value as ChecklistRowApplicability })
                      }
                    >
                      {APPLICABILITY_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {row.origin === "source" || showSource || numericRule ? (
                    <div className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
                      {row.origin === "source" ? (
                        <Badge variant="secondary" className="shrink-0 whitespace-nowrap">
                          From document
                        </Badge>
                      ) : null}
                      {numericRule ? (
                        <Badge variant="outline" title="Checked as a number; editing the label drops this rule">
                          Checks {numericRule}
                        </Badge>
                      ) : null}
                      {showSource ? (
                        <span className="min-w-0 truncate" title={`Found: “${sourceText}”`}>
                          Found: “{sourceText}”
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                  {originReason ? (
                    // A long bulleted note the model skipped and a second check
                    // put back (ENG-2100), or a sentence of running text it put
                    // back (ENG-2101); say why, in full, so the planner can
                    // overrule it.
                    <p className="text-xs text-muted-foreground" data-testid="proposed-row-origin-reason">
                      {row.source_kind === "prose"
                        ? "Written as a sentence, then kept as a requirement"
                        : "Skipped as a note, then kept as a requirement"}
                      : {originReason}
                    </p>
                  ) : null}
                  <AutoGrowTextarea
                    value={row.look_for ?? ""}
                    onChange={(event) => patchRow(row.rowId, { look_for: event.target.value })}
                    disabled={disabled}
                    aria-label={`Look for on the plan for row ${position}`}
                    placeholder="What should a reviewer look for on the plan?"
                    rows={2}
                    className="min-h-[2.5rem] resize-none overflow-hidden text-xs"
                  />
                  {citedDocuments.length > 0 ? (
                    // Truncated with the full list on hover, like the source
                    // line above: a long document name otherwise wraps
                    // mid-filename and makes the rows uneven.
                    <p
                      className="min-w-0 truncate text-xs text-muted-foreground"
                      title={`Based on ${citedDocuments.join(", ")}`}
                      data-testid="proposed-row-sources"
                    >
                      Based on {citedDocuments.join(", ")}
                    </p>
                  ) : row.look_for?.trim() ? (
                    <p className="min-w-0 truncate text-xs text-muted-foreground" data-testid="proposed-row-sources">
                      General guidance; not tied to a local document
                    </p>
                  ) : null}
                </div>
                <div className="flex shrink-0 items-center gap-0.5">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    aria-label={`Move row ${position} up`}
                    disabled={disabled || index === 0}
                    onClick={() => onChange(moveRow(rows, index, -1))}
                  >
                    <ArrowUp className="h-3.5 w-3.5" aria-hidden />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    aria-label={`Move row ${position} down`}
                    disabled={disabled || index === rows.length - 1}
                    onClick={() => onChange(moveRow(rows, index, 1))}
                  >
                    <ArrowDown className="h-3.5 w-3.5" aria-hidden />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive"
                    aria-label={`Remove row ${position}`}
                    disabled={disabled}
                    onClick={() => onChange(rows.filter((candidate) => candidate.rowId !== row.rowId))}
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden />
                  </Button>
                </div>
              </div>
            </li>
          );
        })}
        {rows.length === 0 ? (
          <li className="px-3 py-4 text-center text-xs text-muted-foreground">
            Every row was removed. Add at least one item to confirm.
          </li>
        ) : null}
      </ol>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={() => onChange([...rows, blankProposalRow()])}
        data-testid="add-proposed-row"
      >
        <Plus className="mr-2 h-4 w-4" />
        Add item
      </Button>
    </div>
  );
}
